import {Component, Input, SimpleChanges} from '@angular/core';
import * as $ from 'jquery';
import * as d3 from 'd3';
import {BarChartModel} from '../../../../../models/bar-chart.model';
import * as uuid from 'uuid';
import {CommonService} from '../../../services/common.service';
import {DecimalPipe} from '@angular/common';

@Component({
  selector: 'bar-chart',
  templateUrl: './bar-chart.component.html',
  styleUrls: ['./bar-chart.component.css']
})
export class BarChartComponent {
  chartId = 'x' + uuid.v4();
  chatIdSelector = '#' + this.chartId;

  @Input() chartName: string;
  @Input() chartData: BarChartModel;
  @Input() showTargetLine: boolean;
  @Input() xAxisLabel: string | null;
  @Input() yAxisLabel: string | null;
  @Input() yAxisLabelFormat: string | 'minutesToHrsAndMinutes';
  originalMargins = {top: 2, right: 2, bottom: 3.5, left: 5.5};
  margins = {top: 2, right: 2, bottom: 3.5, left: 5.5};
  width = 100;
  height = 100;
  svg: d3.Selection<SVGGElement, unknown, HTMLElement, any>;
  x: d3.ScaleBand<string>;
  y: d3.ScaleLinear<number, number, never>;
  resizeObserver: ResizeObserver;

  constructor(private commonService: CommonService, private decimalPipe: DecimalPipe) {
    // creating resize observer for detecting size change of chart container
    this.resizeObserver = new ResizeObserver(() => {
      this.renderChart();
    })
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['chartData']?.currentValue) {
      this.renderChart();
    }
  }

  ngOnInit() {
    setTimeout(() => {
      const chartContainer = document.getElementById(this.chartId);
      this.resizeObserver.observe(chartContainer);
    });
  }

  renderChart() {
    if (this.chartData) {
      setTimeout(() => {
        this.createChart();
      })
    }
  }

  createChart() {
    this.width = $(this.chatIdSelector).width();
    this.height = $(this.chatIdSelector).height();
    this.margins.top = this.commonService.convertRemToPx(this.originalMargins.top);
    this.margins.right = this.commonService.convertRemToPx(this.originalMargins.right);
    this.margins.left = this.commonService.convertRemToPx(this.originalMargins.left);
    this.margins.bottom = this.commonService.convertRemToPx(this.originalMargins.bottom);

    const element = document.getElementById(this.chartId);
    if (!element) {
      // component destroyed but ngOnDestroy event is not triggered, so manually triggering
      // NOTE: This is happening when switching between XS <=> SM
      this.ngOnDestroy();
      return
    }
    element.innerHTML = "";

    // ********** DRAWING THE SVG ON SCREEN & ADJUSTING OR SETTING VIEW-BOX WIDTH AND HEIGHT **********//
    this.svg = d3.select(this.chatIdSelector)
      .append('svg')
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('viewBox', `0, 0, ${this.width}, ${this.height}`)
      .attr('preserveAspectRatio', 'xMinYMin')
      .append('g')
      .attr('class', 'main-content')

    // Create the X-axis band scale
    this.x = d3.scaleBand()
      .domain(this.chartData.data.map(item => item.title))
      .range([this.margins.left, this.width - this.margins.right])
      .padding(0.2)

    this.svg.append("text")
      .attr('class', 'x-axis-label')
      .attr("x", this.width / 2)
      .attr("y", this.height)
      .style("text-anchor", "middle")
      .text(this.xAxisLabel);

    // Create the Y-axis band scale
    this.y = d3.scaleLinear()
      .domain(this.getYAxisDomain())
      .range([this.height - this.margins.bottom, this.margins.top])
      .nice();

    this.svg.append("text")
      .attr('class', 'y-axis-label')
      .attr("transform", "rotate(-90)")
      .attr("y", 0)
      .attr("x", 0 - (this.height / 2))
      .attr("dy", "1em")
      .style("text-anchor", "middle")
      .text(this.yAxisLabel);

    // color palette = one color per subgroup
    const color = d3.scaleOrdinal<string>()
      .domain(['0', '1'])
      .range([this.chartData.config.colors.value])

    // ********** Create tooltip ********** //
    const tooltip = d3.select(this.chatIdSelector)
      .append("div")
      .attr('class', 'tooltip')
      .style("position", "fixed")
      .style("visibility", 'hidden')

    // Create and fill the bars
    const selector = this.svg.selectAll('g')
      .data(this.chartData.data)
      .join('g')
      .attr('class', 'bar')
      .append('rect')
      .attr('x', (d) => this.x(d.title))
      .attr('y', (d) => this.y(d.total))
      .attr('width', this.x.bandwidth())
      .attr('height', (d) => d.total ? ((this.height - this.margins.bottom) - this.y(d.total)) : (0))
      .attr('fill', (d) => d.imputed ? this.chartData.config.colors.imputedValue : this.chartData.config.colors.value)
      .on("mouseover", (event: any, d) => {
        return tooltip
          .html(`
            ${d.imputed ? `Estimated<br>` : ``}
            Week ${d.title}<br>
            ${this.formatValue(d.total)}
          `)
          .transition()
          .duration(200)
          .style('top', (event.clientY - (d.imputed ? 75 : 60)) + 'px')
          .style('left', this.commonService.calculateXForPopup(tooltip, event.clientX) + 'px')
          .style("visibility", 'visible');
      })
      .on("mousemove", (event: any, d) => {
        return tooltip
          .transition()
          .duration(0)
          .style('top', (event.clientY - (d.imputed ? 75 : 60)) + 'px')
          .style('left', this.commonService.calculateXForPopup(tooltip, event.clientX) + 'px');
      })
      .on("mouseout", (event: any) => {
        return tooltip
          .transition()
          .duration(0)
          .style("visibility", 'hidden');
      });


    // Draw the X-axis on the DOM
    this.svg.append('g')
      .attr('transform', `translate(0,${this.height - this.margins.bottom})`)
      .call(d3.axisBottom(this.x))
      .selectAll('text')
      .style('text-anchor', 'center')
      .attr('class', 'x-axis-label');

    // Draw the Y-axis on the DOM
    this.svg.append('g')
      .attr('transform', `translate(${this.margins.left},0)`)
      .call(d3.axisLeft(this.y))
      .selectAll('text')
      .attr('class', 'y-axis-label');

    if (this.showTargetLine) {
      this.toggleTargetLine();
    }
  }

  formatValue(value) {
    // minutesToHrsAndMinutes
    if (this.chartName === 'red-light') {
      return `${value} Red Light Foods`;
    } else if (this.chartName === 'physical-activity') {
      return `${this.decimalPipe.transform(value)} Activity Points`;
    } else if (this.chartName === 'screen-time') {
      return `${this.commonService.convertMinutesToHrsAndMinutes(value * 60)} Screen Time`;
    } else if (this.chartName === 'journaling') {
      return `${value} days`;
    }
    return value;
  }

  toggleTargetLine() {
    this.svg.selectAll('.target-line')
      .call((e) => {
        if (e.nodes().length) {
          // already drawn lines
          if (d3.select(`.main-content`).attr('show_target_line') === '0') {
            // Set target line display flag
            d3.select(`.main-content`).attr('show_target_line', '1')
            // hide target line
            d3.selectAll(`.target-line`).style('display', 'block')
          } else {
            // Set target line display flag
            d3.select(`.main-content`).attr('show_target_line', '0')
            // hide target line
            d3.selectAll(`.target-line`).style('display', 'none')
          }
        } else {
          // Draw target line for the first time
          this.svg.selectAll('.main-content')
            .data(this.chartData.data)
            .enter()
            .filter((item) => !!item.target)
            .append('line')
            .attr('class', 'target-line')
            .attr('x1', (d) => this.x(d.title))
            .attr('x2', (d) => this.x(d.title) + this.x.bandwidth())
            .attr('y1', (d) => this.y(d.target))
            .attr('y2', (d) => this.y(d.target))
            .style('stroke-width', '3')
            .style('stroke', (d) => this.chartData.config.colors.targetColor);

          // Set target line display flag
          d3.select(`.main-content`).attr('show_target_line', '1')
        }
      })
  }

  getYAxisDomain() {
    // collect all Y values
    const yValues = [];
    this.chartData.data.forEach((item) => {
      yValues.push(item.total);
      if (item.target) {
        yValues.push(item.target);
      }
    });
    let min = d3.min(yValues, (item) => item);
    let max = d3.max(yValues, (item) => item);

    // if (this.chartData.config.y.padding?.size) {
    //   // check min is falling below 0
    //   if (min - this.chartData.config.y.padding?.size >= 0) {
    //     min -= this.chartData.config.y.padding?.size;
    //   } else {
    //     min = 0;
    //   }
    //   max += this.chartData.config.y.padding?.size;
    // }

    max += max * 0.1;

    if (['red-light', 'screen-time'].includes(this.chartName)) {
      min = 0;
    }
    return [min, max];
  }

  ngOnDestroy() {
    this.resizeObserver.disconnect();
  }
}
